/*
 * Copyright (C) 2018-2019 Nettoken Ltd. All rights reserved.
 *
 * This document is the property of Nettoken Ltd.
 * It is considered confidential and proprietary.
 *
 * This document may not be reproduced or transmitted in any form,
 * in whole or in part, without the express written permission of
 * Nettoken Ltd.
 */
import uuid from 'uuid/v4';
import CryptoWorker from './worker';

const CRYPTO = 'crypto';

// Workers hash map.
const workers = {};

/**
 * @param {string} type
 *
 * @returns {object} Defaults to null.
 */
const createWorkerInstance = type => {
  switch (type) {
    case CRYPTO:
      return new CryptoWorker();
    default:
      return null;
  }
};

/**
 * @param {string} type
 */
const handleOnMessage = type => {
  const worker = workers[type];
  worker.instance.onmessage = event => worker.callbacks.forEach(cb => {
    if (typeof cb === 'function') cb(event);
  });
};

/**
 * @param {string} type
 *
 * @returns {object}
 */
const getWorkerSingleton = type => {
  if (workers[type]) return workers[type];
  const instance = createWorkerInstance(type);
  if (!instance) {
    console.warn(`Invalid worker type "${type}"`);
    return null;
  }
  const callbacks = [];
  workers[type] = { callbacks, instance };
  handleOnMessage(type);
  return workers[type];
};

/**
 * Implements `worker.onmessage()` method. To define a callback normally,
 * you'd do it like this.
 *
 * worker.onmessage = event => console.log(event);
 *
 * However, this means that we can attach only one handler per worker. Since
 * we allow any module to receive the same instance of a worker through
 * `getWorkerPromise()`, we want to allow multiple `onmessage()` handlers, too.
 *
 * @param {string} type
 * @param {function} callback
 *
 * @return {integer} Index of the inserted listener.
 */
const onWorkerMessage = (type, callback) => {
  const worker = getWorkerSingleton(type);
  if (!worker) return null;
  worker.callbacks = [
    ...worker.callbacks,
    callback,
  ];
  return worker.callbacks.length - 1;
};

/**
 * @param {string} type
 * @param {integer} index
 */
// TODO loop from the end and GC all null objects until first index is found.
const removeWorkerListener = (type, index) => {
  const worker = getWorkerSingleton(type);
  if (!worker || index >= worker.callbacks.length) return;
  worker.callbacks[index] = null;
};

/**
 * Used for testing, should not be called in production.
 *
 * @returns {object}
 */
export const dangerouslyGetWorkers = () => workers;

/**
 * Adds a Promise wrapper to Web Workers. Instead of calling `postMessage()`,
 * we can use promise for cleaner API.
 *
 * @param {string} type Worker type.
 *
 * @return {object} Response data.
 */
export const getWorkerPromise = type => {
  const worker = getWorkerSingleton(type);
  if (!worker) return () => Promise.resolve();
  const promise = (args = {}) => new Promise(resolve => {
    const id = uuid();
    args._id = id;

    const index = onWorkerMessage(type, event => {
      const { data } = event;
      if (data._id === id) {
        removeWorkerListener(type, index);
        delete data._id;
        resolve(data);
      }
    });

    worker.instance.postMessage(args);
  });
  return promise;
};

export * from './const';
